import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  BriefStatus,
  OnboardingResultData,
  OnboardingState,
  OnboardingStatusResponse,
  OnboardingPersistState,
} from "../types/onboardingTypes";
import { checkOnboardingStatusThunk } from "../thunks/onboardingThunk";
import { CampaignItemDataType } from "../types/campaignsTypes";
import { CreatorBrief } from "../types/creatorBriefsTypes.tsx";

const initialState: OnboardingState = {
  isLoading: false,
  personas: null,
  error: null,
  campaign: null,
  initialPersonaCount: 0,
  hasGeneratedAIPersonas: false,
  taskId: null,
  onboardingResult: null,
  briefsForGenerate: [],
  isWebsiteChanged: false,
  briefStatuses: {},
};

export const onboardingSlice = createSlice({
  name: "onboarding",
  initialState,
  reducers: {
    setInitialPersonaCount: (state, action: PayloadAction<number>) => {
      state.initialPersonaCount = action.payload;
    },
    setIsWebsiteChanged: (state, action: PayloadAction<boolean>) => {
      state.isWebsiteChanged = action.payload;
    },
    setOnboardingCampaign: (
      state,
      action: PayloadAction<CampaignItemDataType>,
    ) => {
      state.campaign = {
        id: action.payload.id,
        name: action.payload.name,
      };
    },
    setBriefStatuses: (
      state,
      action: PayloadAction<{ id: number; status: BriefStatus }>,
    ) => {
      state.briefStatuses = {
        ...state.briefStatuses,
        [action.payload.id]: action.payload.status,
      };
    },
    clearBriefStatuses: (state) => {
      state.briefStatuses = initialState.briefStatuses;
    },
    setOnboardingResult: (
      state,
      action: PayloadAction<OnboardingResultData>,
    ) => {
      state.onboardingResult = action.payload;
      state.campaign = {
        id: action.payload.campaign_id,
        name: action.payload.campaign_name || "Onboarding Campaign",
      };
    },
    setHasGeneratedAIPersonas: (state) => {
      state.hasGeneratedAIPersonas = true;
    },
    clearOnboardingResult: (state) => {
      state.onboardingResult = null;
      state.campaign = null;
    },
    setBriefsForGenerate: (state, action: PayloadAction<CreatorBrief[]>) => {
      state.briefsForGenerate = action.payload;
      state.briefStatuses = action.payload.reduce(
        (acc: Record<number, BriefStatus>, item) => {
          const briefId = item.id;
          const status = state.briefStatuses[briefId];
          if (status) {
            acc[briefId] = status;
          }
          return acc;
        },
        {},
      );
    },
    setBriefItemForGenerate: (state, action: PayloadAction<CreatorBrief>) => {
      state.briefsForGenerate = [...state.briefsForGenerate, action.payload];
    },
    hydrateOnboardingState: (
      state,
      action: PayloadAction<OnboardingPersistState>,
    ) => {
      state.campaign = action.payload.campaign;
      state.briefsForGenerate = action.payload.briefsForGenerate || [];
      state.briefStatuses = action.payload.briefStatuses || {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        checkOnboardingStatusThunk.fulfilled,
        (state, action: PayloadAction<OnboardingStatusResponse>) => {
          if (action.payload.status === "Success" && action.payload.result) {
            state.onboardingResult = action.payload.result;
            state.campaign = {
              id: action.payload.result.campaign_id,
              name:
                action.payload.result.campaign_name || "Onboarding Campaign",
            };
          } else if (action.payload.status === "Failure") {
            state.error = action.payload.error || "Onboarding process failed.";
            state.campaign = null;
            state.onboardingResult = null;
          }
        },
      )
      .addCase(checkOnboardingStatusThunk.rejected, (state, action) => {
        state.error =
          (action.payload as string) || "Failed to check onboarding status.";
        state.campaign = null;
        state.onboardingResult = null;
      });
  },
});
export const {
  setOnboardingCampaign,
  setInitialPersonaCount,
  setHasGeneratedAIPersonas,
  clearOnboardingResult,
  setOnboardingResult,
  setBriefsForGenerate,
  setBriefItemForGenerate,
  setIsWebsiteChanged,
  setBriefStatuses,
  clearBriefStatuses,
  hydrateOnboardingState,
} = onboardingSlice.actions;

export default onboardingSlice.reducer;
